import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Image, Row, Card, Divider, Tooltip, Col, notification } from 'antd';
import { EditOutlined, LeftOutlined, DragOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';

import CreateUpdateBrandForm from '../../components/CreateUpdateBrandForm';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { getMenuId, getImageFallbackUrl } from '../../utils/helpers';
import DndList from '../../components/DndList/DndList';

const { Meta } = Card;

function BrandDetail(
  {
    // history,
    // match: {
    //   params: { id: brandId },
    // },
  }
) {
  const { id } = useParams();
  const brandId = id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setHeaderComponent } = useContext(LayoutContext);
  const [sortMode, setSortMode] = useState(false);
  const [tempSortedList, setTempSortedList] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { doFetch: updateBrandFetch } = useFetch();
  const { doFetch: uploadBrandImageFetch } = useFetch();
  const { doFetch: fetchSort } = useFetch();
  const { doFetch: fetchLanguages } = useFetch();
  const { doFetch: fetchBrandDetails } = useFetch();

  // Data states
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [languageList, setLanguageList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load all required data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        // Load all data in parallel
        const promises = [
          // Languages
          new Promise((resolve, reject) => {
            fetchLanguages({
              url: URLS.languages,
              onSuccess: resolve,
              onError: reject,
            });
          }),

          // Brand details (includes products)
          new Promise((resolve, reject) => {
            fetchBrandDetails({
              url: `${URLS.brandDetails(brandId)}?get_products=true`,
              onSuccess: resolve,
              onError: reject,
            });
          }),
        ];

        const [languagesData, brandDetailsResponse] = await Promise.all(promises);

        // Transform brand details to match expected format
        const brandData = brandDetailsResponse.brand;
        if (brandData && Array.isArray(brandData.brand_translations)) {
          // Create flattened name properties for form fields
          const nameFields = {};
          brandData.brand_translations.forEach(translation => {
            nameFields[`name.${translation.language_id}`] = translation.name_text;
          });

          const transformedBrand = {
            id: brandData.id,
            ...nameFields, // Include flattened name fields for form
            translations: brandData.brand_translations.map(translation => ({
              lanId: translation.language_id,
              lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
              name: translation.name_text,
            })),
            image: brandData.image_url,
            productsCount: 0,
          };
          setSelectedBrand(transformedBrand);
        }

        setLanguageList(languagesData);

        // Extract and transform products from brand details response
        if (brandData && brandData.products) {
          // Transform products to include name and nameTranslations fields
          const transformedProducts = brandData.products.map(product => {
            // Find the primary name (prefer first language, fallback to any available translation)
            const primaryTranslation = product.product_translations?.find(
              translation => translation.language_id === languagesData[0]?.id
            ) || product.product_translations?.[0];

            const productName = primaryTranslation?.name_text || 'Unnamed Product';

            // Create nameTranslations HTML
            const nameTranslationsHtml = product.product_translations?.reduce((acc, translation) => {
              const languageName = languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`;
              acc += `${languageName}: ${translation.name_text}<br />`;
              return acc;
            }, '') || 'No translations available';

            return {
              ...product,
              name: productName,
              nameTranslations: nameTranslationsHtml,
            };
          });
          setProductList(transformedProducts);
        } else {
          setProductList([]);
        }

        setHeaderComponent(<b>{`Brand ID: ${brandId}`}</b>);

      } catch (error) {
        console.error('Error loading brand detail data:', error);
        setProductList([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      setHeaderComponent(null);
    };
  }, [brandId, fetchLanguages, fetchBrandDetails]);

  const uploadBrandImage = async (brandData, formValues) => {
    if (!formValues?.image?.file?.originFileObj) {
      notification.success({
        placement: 'bottomRight',
        message: 'Brand updated successfully.',
        duration: 3,
      });
      // Invalidate brands cache to refetch the list with updated brand
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      return;
    }

    await uploadBrandImageFetch({
      method: 'FILE_POST',
      url: URLS.uploadBrandImage({ brandId: brandId }),
      params: { image: formValues?.image?.file?.originFileObj },
      showSuccessNotification: true,
      successMessage: 'Brand updated successfully.',
      onSuccess: () => {
        // Invalidate brands cache to refetch the list with updated brand
        queryClient.invalidateQueries({ queryKey: ['brands'] });
        navigate('/brands');
      },
    });
  };

  const updateBrand = async formValues => {
    const translationsObject = languageList.reduce((acc, language) => {
      acc[language.id] = { name_text: formValues[`name.${language.id}`] };
      return acc;
    }, {});

    await updateBrandFetch({
      url: URLS.updateBrand({ brandId }),
      method: 'PUT',
      params: { ...translationsObject },
      onSuccess: data => uploadBrandImage(data, formValues),
    });
  };

  const handleSortChange = newList => {
    setTempSortedList(newList);
    setHasUnsavedChanges(true);
  };

  const handleSaveSortOrder = () => {
    fetchSort({
      url: URLS.productPrioritiesForBrandUpdate({ brandId }),
      method: 'POST',
      params: {
        priorities: tempSortedList.map(product => product.id),
      },
      onSuccess: () => {
        setSortMode(false);
        setHasUnsavedChanges(false);
        setTempSortedList([]);
        // Update the local product list with the new order
        setProductList(tempSortedList);
      },
    });
  };

  const enterSortMode = () => {
    setSortMode(true);
    setTempSortedList([...productList]);
    setHasUnsavedChanges(false);
  };

  const exitSortMode = () => {
    setSortMode(false);
    setTempSortedList([]);
    setHasUnsavedChanges(false);
  };

  if (isLoading) {
    return (
      <Row>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          Loading brand details...
        </div>
      </Row>
    );
  }

  return (
    <>
      <Row>
        {selectedBrand?.id && (
          <CreateUpdateBrandForm
            selectedBrandItem={selectedBrand}
            languageList={languageList}
            onSubmit={updateBrand}
          />
        )}
      </Row>
      <Divider />
      <div className="m-b-30" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        {!sortMode ? (
          <Button
            type="primary"
            icon={<DragOutlined />}
            onClick={enterSortMode}
          >
            Sort Products
          </Button>
        ) : (
          <>
            <Button
              type="dashed"
              icon={<LeftOutlined />}
              onClick={exitSortMode}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSaveSortOrder}
              disabled={!hasUnsavedChanges}
            >
              Save Order
            </Button>
            {hasUnsavedChanges && (
              <span style={{ color: '#faad14', fontSize: '14px' }}>
                You have unsaved changes
              </span>
            )}
          </>
        )}
      </div>
      <Row gutter={[20, 20]} style={{ overflowX: 'auto' }}>
        <DndList
          list={sortMode ? tempSortedList : productList}
          onChange={handleSortChange}
          sortMode={sortMode}
          renderItem={product => (
            <Col {...(sortMode ? { style: { width: '220px' } } : { span: 4 })} key={product.id}>
              <Card
                className="product-card"
                cover={
                  <Image alt="product image" className="product-card-image" src={product.image_url} fallback={getImageFallbackUrl(product.image_url)} />
                }
                actions={[
                  <Tooltip placement="top" title="Update">
                    <Link to={`/product/${product.id}`}>
                      <EditOutlined type="edit" key="edit" />
                    </Link>
                  </Tooltip>,
                ]}
              >
                <Meta
                  title={
                    <Tooltip
                      placement="top"
                      title={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: product.nameTranslations,
                          }}
                        />
                      }
                    >
                      {product.name}
                    </Tooltip>
                  }
                  description={`${product.price} ${product.currency}`}
                />
              </Card>
            </Col>
          )}
        />
      </Row>
    </>
  );
}

export default BrandDetail;
